import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { pathgen } from 'object-path-generator';
import { EnhancedAutocomplete, useOverlayContext } from 'seagull';
import ServiceBuilder from '~/components/ServiceBuilder/ServiceBuilder';
import { useServiceTemplateApi } from '~/hooks/useServiceTemplateApi';
import { useLocaleKeys } from '~/i18n';
import { makeFailure, makeSuccess } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { AgreementDataModel } from '~/models/AgreementDataModel';
import { genericConnector } from '~/utils/react-hook-form.utils';
import { useServiceTemplatesForBilling } from './hooks/useServiceTemplatesForBilling';
export const ServiceTemplateInputTestIds = pathgen('ServiceTemplateInputTestIds');
export const ServiceTemplateInput = (props)=>{
    const { testId, applyServiceChange, renderProps, relationship, menuWidth, excludeBundles = true } = props;
    const serviceTemplateApi = useServiceTemplateApi();
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: serviceTemplateInputTexts } = useLocaleKeys('components.serviceTemplateInput');
    const { field } = renderProps;
    const { serviceTemplates, isLoading, error: serviceTemplatesError, refresh: refreshServiceTemplates } = useServiceTemplatesForBilling({
        refreshOnMount: true
    });
    const options = useMemo(()=>excludeBundles ? serviceTemplates.filter((st)=>!(st === null || st === void 0 ? void 0 : st.bundle)) : serviceTemplates, [
        serviceTemplates,
        excludeBundles
    ]);
    const selectedOption = useMemo(()=>{
        var _options_find;
        return (_options_find = options.find((st)=>st.id === field.value)) !== null && _options_find !== void 0 ? _options_find : null;
    }, [
        options,
        field.value
    ]);
    const openCreateServiceTemplateModal = async ()=>{
        let newService;
        const onSave = async (service)=>{
            try {
                const { id } = await serviceTemplateApi.create(serviceTemplateModel.fromService(service));
                const { data: newServiceTemplates } = await refreshServiceTemplates();
                newService = newServiceTemplates === null || newServiceTemplates === void 0 ? void 0 : newServiceTemplates.find((s)=>s.id === id);
                overlayContext.showSuccessToast(commonTexts.toasts.serviceCreatedSuccessfully(), 'service-created-success');
                return makeSuccess({
                    id
                });
            } catch (error) {
                overlayContext.showErrorToast(commonTexts.toasts.failedToCreateService(), 'service-creation-failed');
                return makeFailure(error);
            }
        };
        await overlayContext.showModal({
            biName: 'service_builder_modal',
            transparentBackdrop: true,
            content: (p)=>/*#__PURE__*/ _jsx(ServiceBuilder, _object_spread_props(_object_spread({}, p), {
                    serviceNameBlacklist: serviceTemplates.map((s)=>s.name),
                    agreementData: AgreementDataModel.fromRelationship(relationship),
                    serviceOrigin: 'template',
                    onSave: (s)=>onSave(s)
                }))
        });
        return newService;
    };
    if (serviceTemplatesError) throw serviceTemplatesError;
    return /*#__PURE__*/ _jsx(EnhancedAutocomplete, {
        bi: {
            name: 'service_template'
        },
        testId: testId !== null && testId !== void 0 ? testId : ServiceTemplateInputTestIds.ServiceTemplateInput(),
        options: options,
        keyField: "id",
        labelField: "name",
        menuWidth: menuWidth,
        size: "small",
        isLoading: isLoading,
        selectedOption: selectedOption,
        error: genericConnector(renderProps).showErrors,
        emptySearchText: serviceTemplateInputTexts.noMatchesFound(),
        textFieldProps: {
            bi: {
                name: 'service_template_name'
            },
            inputRef: field.ref
        },
        applyOptionChange: (service)=>{
            field.onChange(service === null || service === void 0 ? void 0 : service.id);
            applyServiceChange === null || applyServiceChange === void 0 ? void 0 : applyServiceChange(service);
        },
        newItemOption: {
            text: serviceTemplateInputTexts.createNew(),
            onClick: openCreateServiceTemplateModal
        }
    });
};
